import React, {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useEnvironment, WidgetProps, useBi } from '@wix/yoshi-flow-editor';
import { faqQuestionExpanded } from '@wix/bi-logger-labs-data/v2';
import { pauseHeightChanges } from '@wix/yoshi-flow-editor/wix-height-updater';
import { TPAComponentsProvider } from '@wix/wix-ui-tpa/cssVars';
import { ISettingsContextValue } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { st, classes } from './Widget.st.css';

import HeaderWrapper from './HeaderWrapper/HeaderWrapper';
import TabsWrapper from './Tabs/TabsWrapper';
import QuestionsWrapper from './QuestionsWrapper/QuestionsWrapper';
import SearchWrapper from './SearchWrapper/SearchWrapper';
import { AppContext } from './SettingsProvider/SettingsProvider';
import {
  RCVSettingsProvider,
  RCVInstanceProvider,
} from './RCV/RCVSettingsContext';
import settingsParams, { MAX_MOBILE_SIDE_PADDING } from '../settingsParams';
import constants from '../../../constants';
import { AccordionCSS } from './QuestionsWrapper/Interfaces';
import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { ErrorMessage } from './ErrorHandler/ErrorMessage';
import { DATA_HOOKS } from '../../../constants/dataHooksElements';
import { SCROLL_INTO_VIEW_AFTER_TRANSITION_MS } from '../../../utils/scrollToElement';
import stylesParams from '../stylesParams';
import { ControllerProps } from '../../../types/ControllerProps';
import { useLayoutType } from '../../../hooks/useLayoutType';
import { CategoriesPosition } from '../../../enums';

const Widget: FC<WidgetProps<ControllerProps>> = ({
  instance,
  instanceId,
  baseURL,
  siteBaseUrl,
  api: appAPI,
  initialData,
  questionId,
  fromSearch,
  host,
  listCategoriesResponse,
  listQuestionsResponse,
  fetchError,
  searchTermResponse,
  isSEO = false,
  isHasQuestions = true,
  isEditor3 = false,
}) => {
  const logger = useBi();
  const settings: ISettingsContextValue = useSettings();
  const styles = useStyles();
  const { isMobile, isEditor, multilingual, isCssPerBreakpoint, isEditorX } =
    useEnvironment();
  const isResponsive = Boolean(isCssPerBreakpoint || isEditorX);
  // Handle RTL languages
  let isRTL = settings.get(settingsParams.textAlignment) === 'right';
  if (multilingual?.isEnabled) {
    const languageCode = multilingual.currentLanguage?.split('-')[0] || '';
    isRTL = constants.RTL_LANGUAGE.includes(languageCode);
  }

  // Extract settings values

  const {
    layout: { numberLayout, accordionLayout, listLayout },
    categoriesPosition,
    displayContent,
    itemAppearance,
    spacing,
    showTitle,
  } = useLayoutType(isEditor3);
  let { vertical, horizontal } = spacing;
  if (isResponsive && isMobile) {
    if (vertical === 0 && horizontal === 0) {
      vertical = 0;
      horizontal = 20;
    }
  }
  if (isMobile) {
    horizontal = Math.min(horizontal, MAX_MOBILE_SIDE_PADDING);
  }

  const showSearchStyleParam = styles.get?.(stylesParams.showSearchBar) ?? true;
  const showSearch =
    showSearchStyleParam && settings.get(settingsParams.searchBar).show;
  const showFirstAnswer = settings.get(settingsParams.openFirstAnswer);

  // Derived initial expanded question (no effect needed → avoids extra re-render)
  const derivedFirstQuestionId: string | undefined = isEditor
    ? showFirstAnswer
      ? questionId
      : undefined
    : questionId;

  // Define component states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    initialData?.selectedCategory,
  );
  const [userFirstQuestionId, setUserFirstQuestionId] = useState<
    string | undefined
  >(undefined);
  const firstQuestionId = userFirstQuestionId ?? derivedFirstQuestionId;
  const setFirstQuestionId = setUserFirstQuestionId;

  const handleSearchChange = useCallback(
    (term: string) => setSearchTerm(term),
    [],
  );

  // Adjust styles based on settings
  const customStyle: AccordionCSS = {
    '--item-top-bottom-padding': `${vertical}px`,
    '--item-left-right-padding': `${horizontal}px`,
  };
  const showCategoriesSettingsParam =
    displayContent === constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
  const showCategoriesStyleParam =
    styles.get?.(stylesParams.showFAQCategories) ?? true;
  const showCategories =
    (showCategoriesSettingsParam && showCategoriesStyleParam) ||
    searchTerm !== '';
  const isSideCategories = categoriesPosition === CategoriesPosition.SIDE;
  const isTopCategories = categoriesPosition === CategoriesPosition.TOP;

  useEffect(() => pauseHeightChanges(), [host]);

  // Reset user-expanded state when deep link questionId changes
  useEffect(() => {
    setUserFirstQuestionId(undefined);
  }, [questionId]);

  // Handle deep linking and scrolling to the question (BI report only; initial open state is derived above)
  useEffect(() => {
    if (isEditor) {
      return;
    }

    if (!questionId || !fromSearch) {
      return;
    }

    const { questionEntries } = initialData;
    const questionData = questionEntries?.find(
      (q: QuestionEntry) => q.id === questionId,
    );

    if (questionData) {
      const { question, categoryId, sortOrder } = questionData;
      const event = faqQuestionExpanded({
        type: 'deep link',
        question_id: questionId,
        question_name: question || '',
        category_id: categoryId || '',
        question_order: sortOrder || 0,
        url: baseURL || '',
        isOoi: true,
      });
      (logger.report as (e: typeof event) => void)?.(event);
    }
  }, [
    fromSearch,
    isEditor,
    questionId,
    initialData,
    logger,
    baseURL,
    appAPI,
    host,
  ]);
  useEffect(() => {
    if (!questionId || isEditor || !fromSearch) {
      return;
    }
    const scrollToQuestion = (el: HTMLElement) => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    // Delay so DOM is ready and accordion has expanded (firstQuestionId applied).
    // Then: host scrolls page to widget, then we scroll iframe content to the question.
    const delayMs = 350;
    let unsubscribe: (() => void) | undefined;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;
    const timeoutId = setTimeout(() => {
      const element = document.getElementById(questionId);
      if (!element) {
        return;
      }
      try {
        if (host) {
          host
            .scrollTo()
            .then(() => {
              appAPI.scrollToElement(`${questionId}`);
              scrollToQuestion(element);
            })
            .catch(() => {
              scrollToQuestion(element);
            });
          unsubscribe = host?.onSiteReady?.(() => {
            appAPI.scrollToElement(`${questionId}`);
            scrollToQuestion(element);
          });
          // If host.scrollTo() never resolves (e.g. on some Wix pages), still scroll iframe to question
          fallbackId = setTimeout(() => scrollToQuestion(element), 1200);
        } else {
          appAPI.scrollToElement(`${questionId}`);
          scrollToQuestion(element);
        }
      } catch (error) {
        console.warn('host.scrollTo failed:', error);
        scrollToQuestion(element);
      }
    }, delayMs);
    return () => {
      clearTimeout(timeoutId);
      if (fallbackId) {
        clearTimeout(fallbackId);
      }
      unsubscribe?.();
    };
  }, [questionId, isEditor, appAPI, host, fromSearch]);

  // Keep stable reference when question list content is unchanged to avoid extra
  // re-renders down the tree (QuestionsWrapper → AccordionOneColumn).
  const questionsSource =
    searchTerm && searchTermResponse?.questionEntries
      ? searchTermResponse.questionEntries
      : initialData?.questionEntries;
  const questionsRef = useRef<QuestionEntry[]>(questionsSource ?? []);
  const questions = useMemo(() => {
    const next = questionsSource ?? [];
    if (
      questionsRef.current.length === next.length &&
      questionsRef.current.every(
        (q, i) =>
          q.id === next[i]?.id && q.updatedDate === next[i]?.updatedDate,
      )
    ) {
      return questionsRef.current;
    }
    questionsRef.current = next;
    return next;
  }, [questionsSource]);

  const scrollToQuestion = useCallback((targetQuestionId: string) => {
    // Open-one-at-a-time expand: do NOT call host.scrollTo / appAPI.scrollToElement here.
    // Those scroll the host page to the widget anchor while scrollIntoView also adjusts
    // scroll — users see up/down jitter. The widget is already on screen when tapping FAQ rows.
    window.setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.getElementById(targetQuestionId)?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        });
      });
    }, SCROLL_INTO_VIEW_AFTER_TRANSITION_MS);
  }, []);

  const appContextValue = useMemo(
    () => ({
      instance,
      instanceId,
      baseURL,
      selectedCategory,
      setSelectedCategory,
      firstQuestionId,
      setFirstQuestionId,
      questionId,
      appAPI,
      scrollToQuestion,
      searchTerm,
      setSearchTerm,
      listCategoriesResponse,
      listQuestionsResponse,
      searchTermResponse,
      fetchError,
      isEditor3,
    }),
    [
      instance,
      instanceId,
      baseURL,
      selectedCategory,
      firstQuestionId,
      setFirstQuestionId,
      questionId,
      appAPI,
      scrollToQuestion,
      searchTerm,
      listCategoriesResponse,
      listQuestionsResponse,
      searchTermResponse,
      fetchError,
      isEditor3,
    ],
  );

  return (
    <TPAComponentsProvider value={{ rtl: isRTL, mobile: isMobile, seo: isSEO }}>
      <AppContext.Provider value={appContextValue}>
        <RCVInstanceProvider
          metaSiteId={appAPI?.metaSiteId}
          instance={instance}
          siteBaseUrl={siteBaseUrl}
        >
          <RCVSettingsProvider isEditor3={isEditor3}>
            <div
              data-hook={DATA_HOOKS.WIDGET_ROOT}
              dir={isRTL ? 'rtl' : 'ltr'}
              style={customStyle}
              className={st(classes.container, {
                mobile: isMobile,
                isSideCategoriesPosition:
                  searchTerm !== '' ? false : isSideCategories,
                isTopCategoriesPosition: isTopCategories || searchTerm !== '',
                hideSearch: !showSearch || !isHasQuestions,
                hideTitle: !showTitle || !isHasQuestions,
                hideCategories: !showCategories || !isHasQuestions,
                theme: itemAppearance,
              })}
            >
              {isHasQuestions && <HeaderWrapper isEditor3={isEditor3} />}
              {isHasQuestions && (
                <SearchWrapper onSearchChange={handleSearchChange} />
              )}
              {isHasQuestions && (
                <TabsWrapper validLists={initialData.validLists} />
              )}
              <QuestionsWrapper
                questions={questions}
                fetchError={fetchError}
                searchTerm={searchTerm}
                numberLayout={numberLayout}
                accordionLayout={accordionLayout}
                listLayout={listLayout}
              />
              <ErrorMessage error={fetchError} />
            </div>
          </RCVSettingsProvider>
        </RCVInstanceProvider>
      </AppContext.Provider>
    </TPAComponentsProvider>
  );
};

export default Widget;
